import { Skeleton } from '@client/components/ui/skeleton';
import { Card } from '@client/components/ui/card';

/**
 * Skeletons are shaped like the content they stand in for.
 *
 * A skeleton that doesn't match the real layout doesn't remove the flicker, it just moves it: the
 * placeholder has no border, the data arrives inside a bordered card, and everything below jumps. So
 * these carry the same chrome as the components they replace — `PANEL` is literally the class string
 * the real panels use, and the row paddings match row for row.
 */
const PANEL = 'overflow-hidden rounded-xl border bg-card shadow-card';

export function CardSkeleton() {
  return (
    <Card className="p-6">
      <Skeleton className="h-4 w-24 mb-3" />
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-3 w-32" />
    </Card>
  );
}

/** Matches the framed tables in Pacientes and Contatos: header bar, divided rows, px-4 py-2. */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className={PANEL}>
      <div className="flex border-b bg-muted/40 px-4 py-2">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="flex-1 pr-4"><Skeleton className="h-3 w-20" /></div>
        ))}
      </div>
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex px-4 py-2">
            {Array.from({ length: cols }).map((_, j) => (
              <div key={j} className="flex-1 pr-4">
                {/* First column is a name and reads darker; the rest are dimmed metadata. */}
                <Skeleton className={j === 0 ? 'h-4 w-32' : 'h-3 w-16 opacity-70'} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A record page: a grid of bordered section cards, some full-width.
 *
 * The patient record used `TableSkeleton`, which draws a framed table with a header bar — for a
 * two-column grid of cards. Everything jumped when the data arrived, which is exactly the failure
 * the note at the top of this file warns about. The shape here is Pacientes' own section grid:
 * `sm:grid-cols-2` with the wide sections spanning both.
 */
export function RecordSkeleton() {
  const section = (lines: number, wide = false) => (
    <div className={`${PANEL} p-4 ${wide ? 'sm:col-span-2' : ''}`}>
      <Skeleton className="mb-3 h-4 w-24" />
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} className={`h-3 ${i === 0 ? 'w-3/4' : 'w-1/2'} opacity-70`} />
        ))}
      </div>
    </div>
  );
  return (
    <>
      <div className="mb-6 space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-3 w-32 opacity-70" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {section(1, true)}
        {section(3, true)}
        {section(2)}
        {section(2)}
        {section(2, true)}
      </div>
    </>
  );
}

/**
 * Plain rows, for lists that sit inside a panel someone else drew (the settings tabs) — or framed, for
 * the ones that draw their own (Fila).
 */
export function ListSkeleton({ rows = 5, framed = false }: { rows?: number; framed?: boolean }) {
  const body = (
    <div className={framed ? 'divide-y' : 'space-y-1'}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="h-4 flex-1 max-w-[220px]" />
          <Skeleton className="hidden h-3 flex-1 max-w-[260px] opacity-70 sm:block" />
          <Skeleton className="h-3 w-12 shrink-0 opacity-70" />
        </div>
      ))}
    </div>
  );
  return framed ? <div className={PANEL}>{body}</div> : body;
}

/** The inbox sidebar: channel icon, name over preview, timestamp right. */
export function ConversationListSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3 border-l-2 border-transparent px-3 py-3">
          <Skeleton className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-baseline gap-2">
              <Skeleton className="h-4 flex-1 max-w-[140px]" />
              <Skeleton className="h-3 w-8 shrink-0 opacity-70" />
            </div>
            <Skeleton className="h-3 w-full max-w-[180px] opacity-70" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Hoje's sections: titled header with a count, then divided rows. */
export function BlockSkeleton({ blocks = 2, rows = 3 }: { blocks?: number; rows?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: blocks }).map((_, i) => (
        <section key={i} className={PANEL}>
          <header className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-3 w-12 opacity-70" />
          </header>
          <div className="divide-y">
            {Array.from({ length: rows }).map((_, j) => (
              <div key={j} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 flex-1 max-w-[180px]" />
                <Skeleton className="hidden h-3 flex-1 max-w-[220px] opacity-70 sm:block" />
                <Skeleton className="h-3 w-10 shrink-0 opacity-70" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function KanbanSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="w-72 shrink-0 space-y-3">
          <Skeleton className="h-6 w-32" />
          {/* Decreasing counts per column: real boards are never evenly filled. */}
          {Array.from({ length: 3 - i }).map((_, j) => (
            <Card key={j} className="p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2 opacity-70" />
              <Skeleton className="h-3 w-full opacity-70" />
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ChatSkeleton() {
  return (
    <div className="flex h-full">
      <div className="w-72 shrink-0 border-r">
        <div className="p-3"><Skeleton className="h-9 w-full" /></div>
        <ConversationListSkeleton rows={6} />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex-1 space-y-3 p-4">
          <div className="flex justify-start"><Skeleton className="h-12 w-64 rounded-xl" /></div>
          <div className="flex justify-end"><Skeleton className="h-12 w-48 rounded-xl" /></div>
          <div className="flex justify-start"><Skeleton className="h-12 w-56 rounded-xl" /></div>
          <div className="flex justify-end"><Skeleton className="h-10 w-40 rounded-xl" /></div>
        </div>
        <div className="border-t p-3"><Skeleton className="h-11 w-full" /></div>
      </div>
    </div>
  );
}
